/**
 * ERP-104 — колонка «Фактуры» в ростере группы: выставлена ли фактура по
 * каждому взносу договора, без захода в карточку каждого студента.
 *
 * «Фактура выставлена» — это ТОЛЬКО серверный `hasVatInvoice` (настоящая
 * выставленная VAT-фактура: не проформа, не корректировка, не отклонённая —
 * `fakturownia.services.billability.is_billing_document`). Статус взноса
 * `invoiced` сюда не годится: сервер ставит его на ЛЮБОЙ прикреплённый
 * документ, проформу в том числе. Второй копии правила на клиенте нет.
 *
 * Список `invoices` у роли без доступа к Fakturownia (HR) приходит пустым, а
 * `hasVatInvoice` — честным: счётчик считается только по флагу, номера —
 * бонус для тех, кому они видны.
 *
 * «Нет фактуры после срока» горит только там, где фактуру ещё есть смысл
 * выставлять: взнос, уже оплаченный без VAT-фактуры (вручную или по
 * проформе — `paid` / `paid_manual` / `partial`), ERP задним числом не
 * «довыставит», и такой взнос — нейтральное «Оплачено без фактуры».
 */

import { toApiDate, todayInWarsaw } from './zoomTime';

/** Тон счётчика — подмножество `TagTone` из `_detailShared/detail.styled`. */
export type RosterInvoiceTone = 'ok' | 'warn' | 'neutral';

/**
 * Что показывает ячейка:
 *  - `free` — бесплатный договор, фактур по нему не ждём;
 *  - `prefinanced` — госфинансирование, фактуры выставляются вне ERP;
 *  - `noSchedule` — у договора нет ни одного взноса;
 *  - `schedule` — счётчик «N/M» по графику взносов.
 */
export type RosterInvoiceState =
  | 'free'
  | 'prefinanced'
  | 'noSchedule'
  | 'schedule';

/** Минимум взноса, нужный для подсчёта. */
export interface IRosterInvoiceContribution {
  date?: string | null;
  hasVatInvoice: boolean;
  /** Серверный статус взноса (строка в нижнем регистре). */
  status?: string | null;
}

/**
 * Отметка одного взноса в списке:
 *  - `issued` — выставлена VAT-фактура;
 *  - `paidWithoutInvoice` — оплачен (целиком или частично) без VAT-фактуры;
 *  - `missingPast` — фактуры нет, срок прошёл;
 *  - `missingFuture` — фактуры нет, срок впереди.
 */
export type RosterInvoiceMark =
  | 'issued'
  | 'paidWithoutInvoice'
  | 'missingPast'
  | 'missingFuture';

/** Статусы, при которых деньги по взносу уже пришли. */
const PAID_STATUSES = ['paid', 'paid_manual', 'partial'];

/** Минимум договора, нужный для подсчёта (строка ростера подходит как есть). */
export interface IRosterInvoiceSource {
  freeOfCharge?: boolean | null;
  statePrefinanced?: boolean | null;
  terminated?: boolean | null;
  scheduleContributions?: ReadonlyArray<IRosterInvoiceContribution> | null;
}

export interface IRosterInvoiceSummary {
  state: RosterInvoiceState;
  /** Взносов с выставленной VAT-фактурой. */
  issued: number;
  /** Всего взносов в графике. */
  total: number;
  /** Взносов без фактуры, чей срок уже прошёл. */
  missingPast: number;
  tone: RosterInvoiceTone;
}

/**
 * Срок взноса прошёл: дата строго раньше сегодняшней (то же правило, что у
 * долга с ТЗ 0036 — взнос, чей срок сегодня, ещё не просрочен). Даты — ISO
 * `YYYY-MM-DD`, сравниваются как строки.
 */
export const isContributionPast = (
  date: string | null | undefined,
  today: string,
): boolean => Boolean(date) && String(date).slice(0, 10) < today;

/**
 * «Сегодня» по Варшаве (`YYYY-MM-DD`): сроки взносов — польские даты, и
 * браузер в другом часовом поясе не должен сдвигать «срок прошёл» на день.
 */
export const warsawTodayIso = (): string => toApiDate(todayInWarsaw());

/** Отметка взноса — одна на всё: счётчик, тон и список считают по ней. */
export const rosterInvoiceMark = (
  c: IRosterInvoiceContribution,
  today: string,
): RosterInvoiceMark => {
  if (c.hasVatInvoice) return 'issued';
  if (PAID_STATUSES.includes((c.status || '').toLowerCase())) {
    return 'paidWithoutInvoice';
  }
  return isContributionPast(c.date, today) ? 'missingPast' : 'missingFuture';
};

/**
 * Сводка по одному договору. `today` — дата по Варшаве в `YYYY-MM-DD`
 * (`warsawTodayIso()`; параметром, чтобы функция оставалась чистой).
 *
 * Тон: все взносы с фактурой — `ok`; есть взнос без фактуры после срока (и
 * не оплаченный без неё) — `warn`; иначе — `neutral`. У расторгнутого
 * договора недостающая фактура уже не действие — тон не выше `neutral`.
 */
export const summarizeRosterInvoices = (
  row: IRosterInvoiceSource,
  today: string,
): IRosterInvoiceSummary => {
  const schedule = row.scheduleContributions ?? [];
  const total = schedule.length;
  const issued = schedule.filter((c) => c.hasVatInvoice).length;
  const missingPast = schedule.filter(
    (c) => rosterInvoiceMark(c, today) === 'missingPast',
  ).length;

  let state: RosterInvoiceState = 'schedule';
  if (row.freeOfCharge) state = 'free';
  else if (row.statePrefinanced) state = 'prefinanced';
  else if (total === 0) state = 'noSchedule';

  let tone: RosterInvoiceTone = 'neutral';
  if (state === 'schedule') {
    if (issued === total) tone = 'ok';
    else if (missingPast > 0 && !row.terminated) tone = 'warn';
  }

  return { state, issued, total, missingPast, tone };
};
